<script setup lang="ts">
/**
 * Aura Incorporation — Constitution (신버전 전체 정관의 fill-point 뷰).
 *   법정 본문(26p)은 템플릿 docx 에 있고, 화면은 채워지는 값만 보여 준다:
 *   회사명 · 등록사무소 · 발행자본 · 가입자(subscribers, Client Acceptance 에서) · 가입자 서명선.
 *   subscribers 표/서명선은 [[if shareholders]] 루프 + shareholder_name_1/2(서명선) 로 생성된다.
 */
import { computed } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill } from './cdd/useCddFill';
import { cafRowIds } from './cdd/cafRows';
import './allotDoc.css';

const fill = useCddFill();

/**
 * 가입자(subscribers) = 주주(Client Acceptance sh${i}).
 * 이름·주식수 칸은 CAF 와 **같은 키**(sh{i}_shareholder_name / _number_of_shares)를 쓰는 입력칸이라
 * 여기서 고치면 CAF·First Board 배정표·Share Application 에도 반영되고, 반대도 마찬가지다.
 */
const subscribers = computed(() =>
  cafRowIds(fill.values, 'sh', 'shareholder_name').map((i) => ({
    idx: i,
    name: String(fill.values[`sh${i}_shareholder_name`] ?? '').trim() || `Shareholder ${i}`,
  })),
);
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-meta">THE COMPANIES ACT (CHAPTER 50)</div>
      <div class="doc-meta">A PRIVATE COMPANY LIMITED BY SHARES</div>
      <div class="doc-lbl-title" style="margin-top:6px">CONSTITUTION OF <CddVarField field-key="company_name" label="Company Name" /></div>
    </div>

    <p class="doc-p">The name of the company is <CddVarField field-key="company_name" label="Company Name" />.</p>
    <p class="doc-p">The registered office of the company is situated in the Republic of Singapore.</p>
    <p class="doc-p"><CddVarField field-key="registered_office_address" label="Registered Office Address" /></p>
    <p class="doc-p">The liability of the members is limited.</p>

    <p class="doc-sec-title" style="text-align:left">Share Capital</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Currency</td><td>SGD</td></tr>
        <tr><td class="cdd-lcell">Amount of Issued Share Capital (SGD)</td><td><CddVarField field-key="entity_share_capital" label="Issued Share Capital" /></td></tr>
      </tbody>
    </table>

    <p class="doc-sec-title" style="text-align:left">Subscribers</p>
    <p class="doc-p doc-note">
      We, the persons whose names and occupations are set out below, desire to form a company and each agree to take the number of shares set against our names.
    </p>
    <table class="cdd-tbl">
      <thead><tr><th>Name of Subscribers</th><th>Number of Shares Allotted</th><th>Class</th><th>Currency</th></tr></thead>
      <tbody>
        <tr v-for="row in subscribers" :key="row.idx">
          <td><CddVarField :field-key="`sh${row.idx}_shareholder_name`" :label="`Shareholder ${row.idx} Name`" /></td>
          <td><CddVarField :field-key="`sh${row.idx}_shareholder_number_of_shares`" :label="`Number of Shares — ${row.name}`" /></td>
          <td class="fixed-cell">Ordinary</td><td class="fixed-cell">SGD</td>
        </tr>
        <tr v-if="!subscribers.length">
          <td colspan="4" class="allot-empty">Enter shareholders in the Client Acceptance form first — they appear here automatically.</td>
        </tr>
      </tbody>
    </table>
    <p class="doc-note link-note">Linked with the Client Acceptance form — editing a name or share count here updates it there (and in First Board / Share Application), and vice versa. Class and Currency are fixed wording in the Constitution.</p>

    <p class="doc-sec-title" style="text-align:left">Subscriber Signatures</p>
    <div style="display:flex; gap:40px; margin-top:8px; flex-wrap:wrap">
      <div v-for="row in subscribers.slice(0, 2)" :key="row.idx">
        <div class="sign-underline"></div>
        <div class="doc-note"><CddVarField :field-key="`sh${row.idx}_shareholder_name`" :label="`Shareholder ${row.idx} Name`" /></div>
      </div>
      <div v-if="!subscribers.length" class="doc-note">Subscriber signature lines appear once shareholders are entered.</div>
    </div>

    <p class="doc-p doc-note" style="margin-top:12px">
      Full statutory Constitution (interpretation, shares, general meetings, directors, accounts, etc.) is included in the generated document.
    </p>
  </div>
</template>

<style scoped>
.doc-note { font-size: 10.5px; color: #555; }
.allot-empty { color: #777; font-style: italic; text-align: center; padding: 10px 6px; }
.sign-underline { border-top: 1px solid #333; width: 180px; margin-top: 22px; }
.fixed-cell { color: #666; }
.link-note { margin: 4px 0 0; font-style: italic; }
</style>
